import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { ProductTestingEntity, ProductTestingSchema } from "@/mk2/apps/forum/schemas";
import TestingDetail from "@/mk2/apps/testings/containers/TestingDetail/TestingDetail";
import { testingDetailFetchTrigger } from "@/mk2/apps/testings/containers/TestingDetail/TestingDetail.actions";
import testingsDetailReducer from "@/mk2/apps/testings/containers/TestingDetail/TestingDetail.reducers";
import saga from "@/mk2/apps/testings/containers/TestingDetail/TestingDetail.sagas";
import { getTestingDetailState } from "@/mk2/apps/testings/containers/TestingDetail/TestingDetail.selectors";
import { testingFinishedPostEditUrl, testingsHomeUrl } from "@/mk2/apps/testings/urls";
import { getSlugFromHashtag } from "@/mk2/apps/testings/utils";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { PageRouteProps } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { BlogPostEntity, BlogPostSchema } from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    EDIT_TESTING_FINISHED_POST,
    SEO_DESCRIPTION_COUNSELLINGS,
    SEO_TITLE_TESTINGS,
} from "@/mk/autogenerated/translation";

const fasPencilLg: FontAwesomeIconProps = { icon: faPencil, size: "lg" };

interface OwnProps {}

interface StateProps {
    testing: ProductTestingEntity;
    testersPosts: BlogPostEntity[];
    testingHashtag: string;
    isMobile: boolean;
    loadingState: LoadingState;
    errorMessage: string;
    requestUserPermissions: string[];
    baseUrl: string;
}

interface DispatchProps {
    onLoad(testingHashtag: string);
}

type RouteProps = PageRouteProps<{ slug: string }>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class TestingDetailPage extends Page<OwnProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const testingHashtag = ("test_" + ownProps.routeParams.slug).replace(/-/g, "_");

            const detailState = getTestingDetailState(state, testingHashtag);
            const testing = getDenormalizedEntity<ProductTestingEntity>(
                state,
                ProductTestingSchema,
                detailState.testingId,
            );
            const testersPosts = getDenormalizedEntities<BlogPostEntity>(
                state,
                BlogPostSchema,
                detailState.testersPostsIds,
            );

            return {
                testing,
                testersPosts,
                testingHashtag,
                loadingState: detailState.loadingState,
                errorMessage: detailState.errorMessage,
                isMobile: getRequestDeviceMobile(state),
                requestUserPermissions: getRequestPermissions(state),
                baseUrl: getRequestBaseUrl(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: testingDetailFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        return props.testing?.finishedTitle || SEO_TITLE_TESTINGS;
    }

    public getMetaDescription(props: Props): string {
        return SEO_DESCRIPTION_COUNSELLINGS;
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return baseUrl + testingsHomeUrl;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { requestUserPermissions, testing } = props;

        if (!testing) {
            return null;
        }

        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        if (isAdmin) {
            return (
                <Btn
                    type={BtnType.Admin}
                    label={EDIT_TESTING_FINISHED_POST}
                    icon={fasPencilLg}
                    link={url(testingFinishedPostEditUrl, {
                        slug: getSlugFromHashtag(testing.trackedHashtag),
                    })}
                />
            );
        }
    }

    public render(props: Props): false | JSX.Element {
        return (
            <span>
                <TestingDetail {...props} />
            </span>
        );
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.testings.testingDetail", testingsDetailReducer);
        store.injectSaga("containers.testings.detail", saga);
    }
}
